import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6 } from "styled-components";
import type { Mode } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { Grid } from "~/features/ui/grid";
import { Headline } from "~/features/ui/headline";
import { Eyebrow } from "~/features/ui/eyebrow";
import { isLinkValid } from "~/features/ui/link/utils";
import { Button, type ButtonVariant } from "~/features/ui/button";
import { Link } from "~/features/ui/link";
import { takeFirst } from "~/features/app/utils";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
export type HeadlineDBlockViewProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  eyebrow?: string | null;
  hasSolidBackground?: boolean | null;
  hasSpacer?: boolean | null;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  isHighlighted?: boolean | null;
  link?: PlainLink | Array<PlainLink | null | undefined> | null;
  linkVariant?: string | null;
  mode?: string | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  theme?: string | null;
};
export function HeadlineDBlockView({
  anchor,
  eyebrow,
  hasSolidBackground = false,
  hasSpacer = false,
  hasTopDivider = true,
  headline,
  isHighlighted = false,
  link,
  linkVariant,
  mode = "light",
  sectionProps,
  theme = "white"
}: HeadlineDBlockViewProps) {
  const cta = takeFirst(link);
  const icon = getPlainLinkIcon(cta);
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[{
    "paddingLeft": "var(--spacing-xl)",
    "paddingRight": "var(--spacing-xl)",
    "paddingTop": "var(--spacing-xl)",
    "paddingBottom": "var(--spacing-xl)",
    "color": "var(--text-primary)",
    "@media (min-width: 1024px)": {
      "paddingLeft": "var(--spacing-3xl)",
      "paddingRight": "var(--spacing-3xl)",
      "paddingTop": "var(--spacing-2xl)",
      "paddingBottom": "var(--spacing-2xl)"
    }
  }, hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }, hasSpacer && {
    "paddingTop": "var(--spacing-4xl) !important",
    "@media (min-width: 1024px)": {
      "paddingTop": "28rem !important"
    }
  }, hasSolidBackground && {
    "position": "relative"
  }, theme === "grey" ? {
    "backgroundColor": "var(--bg-tertiary)"
  } : {
    "backgroundColor": "var(--bg-primary)"
  }, isHighlighted && {
    "backgroundColor": "var(--white-static)"
  }]}>
      <_StyledWrap>
        <_StyledGrid $_css2={[{
        "display": "grid",
        "gridTemplateColumns": "repeat(1, minmax(0, 1fr))",
        "gap": "var(--spacing-xl)",
        "@media (min-width: 1024px)": {
          "gridTemplateColumns": "repeat(12, minmax(0, 1fr))",
          "gap": "var(--spacing-3xl)"
        }
      }]}>
          <_StyledDiv>
            {eyebrow && <Eyebrow text={eyebrow} mode={mode as Mode} />}
          </_StyledDiv>

          <_StyledDiv2>
            {headline && <_StyledHeadline type="h2" text={headline} />}
            {isLinkValid(cta) && <Button as={Link} {...cta} icon={icon} variant={linkVariant as ButtonVariant} />}
          </_StyledDiv2>
        </_StyledGrid>
      </_StyledWrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-zcg1s3-0"
})`${p => p.$_css}`;
var _StyledWrap = _styled2(Wrap).withConfig({
  componentId: "sc-sf-zcg1s3-1"
})({
  "borderWidth": "0px"
});
var _StyledGrid = _styled3(Grid).withConfig({
  componentId: "sc-sf-zcg1s3-2"
})`${p => p.$_css2}`;
var _StyledDiv = _styled4("div").withConfig({
  componentId: "sc-sf-zcg1s3-3"
})({
  "position": "relative",
  "zIndex": "10",
  "gridColumn": "1 / -1",
  "display": "flex",
  "flexDirection": "column",
  "@media (min-width: 1024px)": {
    "gridColumn": "span 6 / span 6"
  }
});
var _StyledDiv2 = _styled5("div").withConfig({
  componentId: "sc-sf-zcg1s3-4"
})({
  "gridColumn": "1 / -1",
  "display": "flex",
  "flexDirection": "column",
  "alignItems": "flex-start",
  "gap": "var(--spacing-lg)",
  "@media (min-width: 1024px)": {
    "gridColumn": "span 6 / span 6",
    "gap": "var(--spacing-2xl)"
  }
});
var _StyledHeadline = /*#__PURE__*/_styled6(Headline).withConfig({
  displayName: "headline-d___StyledHeadline",
  componentId: "sc-12aen4k-0"
})({
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});